import PropTypes from 'prop-types';
import { Filter, FilterButton } from "components/filtering";
import { FormListSelector, FormTextInput, FormToggleList } from "components/form-fields";
import { useDebounce } from "hooks/useDebounce";
import React, { useEffect, useRef, useState } from "react";
import { ShowHide } from 'components/layout';
import { useSelector, useDispatch } from 'react-redux';
import { AccountService } from 'services';
import { NotificationToaster } from 'components/notifications';
import { Intent } from '@blueprintjs/core';
import { SetFiltering } from 'state/actions';
import axios from 'axios';
import { PcnService } from 'services/PcnService';
import { ListingTable } from 'components/listing';
import { PcnListingType } from 'pages/pcn/admin/listing/shared/PcnListingType';
import { DateToLocal } from 'components/dates';
import moment from 'moment';
import { Link } from 'react-router-dom';

export function PcnListingResults({ pcnStateToShow }) {

    const debounceTimeout = 1500;
    const badRequestCode = 400;
    const generalErrorMessage = "An error occurred while searching for PCNs. Please try again.";
    const isOnHold = pcnStateToShow === PcnListingType.ONHOLD;

    const [showFilters, setShowFilters] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);
    const [isLoading, setIsLoading] = useState(true);
    const [initalSetupComplete, setInitalSetupComplete] = useState(false);

    const [appliedFilters, setAppliedFilters] = useState([]);
    const [pageFilters, setPageFilters] = useState([]);
    const [deadlineStatuses, setDeadlineStatuses] = useState([]);
    const [selectedDeadlineStatus, setSelectedDeadlineStatus] = useState(null);

    const [totalRecords, setTotalRecords] = useState(0);
    const [tableRows, setTableRows] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);

    const accountsReduxName = "pcnListingAccounts";
    const sortingReduxName = "pcnSorting";
    const reduxAccounts = useSelector(state => state.filters[accountsReduxName]);
    const reduxSorting = useSelector(state => state.filters[sortingReduxName]);
    const [accounts, setAccounts] = useState([]);
    const [selectedAccounts, setSelectedAccounts] = useState([]);

    const inputSearchRef = useRef(null);

    const [statuses, setStatuses] = useState([]);
    const [statusComments, setStatusComments] = useState([]);
    const [authorities, setAuthorities] = useState([]);
    const [issueTypes, setIssueTypes] = useState([]);

    const tableHeaders = [
        "PCN Number",
        "Account",
        ...(isOnHold ? [] : ["Review Date"]),
        "Payment Due Date",
        "Issue Date",
        "PCN Code",
        "Issue Type",
        "Issuing Authority",
        "Registration",
        "Location",
        ...(isOnHold ? [] : ["Status",
            "Status Comment"]),
        "Received Date",
    ];

    const sortableHeaders = [
        { headerName: "PCN Number", sortName: "PCNNUMBER" },
        { headerName: "Account", sortName: "ACCOUNT" },
        ...(isOnHold ? [] : [{ headerName: "Review Date", sortName: "REVIEWDATE" }]),
        { headerName: "Payment Due Date", sortName: "PAYMENTDUEDATE" },
        { headerName: "Issue Date", sortName: "ISSUEDATE" },
        { headerName: "PCN Code", sortName: "PCNCODE" },
        { headerName: "Issue Type", sortName: "ISSUETYPE" },
        { headerName: "Issuing Authority", sortName: "ISSUINGAUTHORITY" },
        { headerName: "Registration", sortName: "REGISTRATION" },
        { headerName: "Location", sortName: "ASSETLOCATION" },
        ...(isOnHold ? [] : [{ headerName: "Status", sortName: "STATUS" },
        { headerName: "Status Comment", sortName: "STATUSCOMMENT" }]),
        { headerName: "Received Date", sortName: "RECEIVEDDATE" },
    ];

    const [sortedBy, setSortedBy] = useState(null);
    const [sortedDir, setSortedDir] = useState(null);

    const currentPageNumber = useRef();
    const currentTableSortBy = useRef();
    const currentTableSortDir = useRef();
    const currentAppliedFilters = useRef();
    const currentAccounts = useRef();
    const currentStatus = useRef();
    const currentSearchTerm = useRef();

    const [filterName, setFilterName] = useState(null);

    const dispatch = useDispatch();

    useEffect(() => {

        setSortedBy(reduxSorting?.field ?? sortableHeaders[2]);
        setSortedDir(reduxSorting?.direction ?? "A");

    }, [reduxSorting]);

    useEffect(() => {
        if (pcnStateToShow == null || pcnStateToShow.length === 0) {
            return;
        }

        const pcnFeatureId = 3;

        const onLoadError = () => {
            NotificationToaster.show(Intent.DANGER, "Could not load search options. Please refresh the page.");
        }

        switch (pcnStateToShow) {
            case PcnListingType.OPEN:
                setFilterName("pcnOpenListing");
                break;
            case PcnListingType.CLOSED:
                setFilterName("pcnClosedListing");
                break;
            default:
                break;
        }

        axios.all([AccountService.getAllAccounts([pcnFeatureId], "name"), PcnService.getOptions()]).then(
            axios.spread(function (accountResponse, optionsResponse) {

                let optionStatuses = [];

                if (pcnStateToShow === PcnListingType.OPEN) {
                    optionStatuses = optionsResponse.statuses.filter(x => x.open === true);
                } else if (pcnStateToShow === PcnListingType.CLOSED) {
                    optionStatuses = optionsResponse.statuses.filter(x => x.open === false);
                }

                setStatuses(optionStatuses);

                const optionSorter = (a, b) => a.name.localeCompare(b.name);

                const issueTypeFilters = optionsResponse.issueTypes.sort(optionSorter).map((it) => { return { "id": it.key, "name": it.name } });
                const statusFilters = optionStatuses.sort(optionSorter).map((it) => { return { "id": it.key, "name": it.name } });
                const statusCommentFilters = optionsResponse.statusComments.sort(optionSorter).map((it) => { return { "id": it.key, "name": it.name } });

                setPageFilters([
                    {
                        name: "STATUS",
                        displayName: "Status",
                        items: statusFilters
                    },
                    {
                        name: "STATUSCOMMENT",
                        displayName: "Status Comment",
                        items: statusCommentFilters
                    },
                    {
                        name: "ISSUETYPE",
                        displayName: "Issue Type",
                        items: issueTypeFilters
                    }
                ]);

                setStatusComments(optionsResponse?.statusComments ?? []);
                setAuthorities(optionsResponse?.issuingAuthorities ?? []);
                setIssueTypes(optionsResponse?.issueTypes ?? []);
                setDeadlineStatuses(optionsResponse?.deadlineStatuses?.map((ds) => {
                    return {
                        id: ds.id,
                        name: ds.name,
                        icon: ds.displayIcon,
                        colour: ds.displayColour,
                        key: ds.key
                    };
                }) ?? []);

                setAccounts(accountResponse.data.filter(a => a != null).map(a => ({ id: a.id, name: `${a.name} - ${a.memberNumber}` })));
                setIsLoading(false);
            }),
            onLoadError
        )
            .catch(onLoadError)
            .finally(() => {
                setIsLoading(false);
            });
    }, [pcnStateToShow]);

    useEffect(() => {

        if (accounts.length > 0 && (pcnStateToShow === PcnListingType.ONHOLD || (statusComments.length > 0 && statuses.length > 0 && authorities.length > 0))) {
            setInitalSetupComplete(true);
        }
    }, [statusComments, statuses, authorities, accounts]);


    function onSearchChange(item) {
        setSearchTerm(item.target.value);
    }

    function onToggleFilter() {
        setShowFilters((prevState) => {
            return !prevState;
        });
    }

    function onFilterChange(listingFilters) {
        setAppliedFilters(listingFilters);
    }

    function onDeadlineStatusChange(key) {
        setSelectedDeadlineStatus(key);
    }

    function onAccountsSelected(selectedItems) {
        dispatch(SetFiltering(accountsReduxName, selectedItems));
    }

    useEffect(() => {
        if (reduxAccounts != null) {
            setSelectedAccounts(reduxAccounts);
        }
    }, [reduxAccounts]);

    function onPagingChange(newPageNumber) {
        setPageNumber(newPageNumber);
    }

    function onTableSort(header, direction) {
        dispatch(SetFiltering(sortingReduxName, {
            field: header,
            direction
        }));
    }

    function fetchTableData(forceRefresh) {

        if (initalSetupComplete && reduxAccounts != null && sortedBy != null) {
            if (
                currentPageNumber.current === pageNumber &&
                currentTableSortBy.current === sortedBy &&
                currentTableSortDir.current === sortedDir &&
                currentSearchTerm.current === debouncedSearchTerm &&
                currentAppliedFilters.current === appliedFilters &&
                currentAccounts.current === reduxAccounts?.sort()?.join(',') &&
                currentStatus.current === selectedDeadlineStatus &&
                !forceRefresh
            ) {
                //If none of the values have changed, then 2 have been updated at once, so prevent the listing from performing another call.
                return;
            }

            currentPageNumber.current = pageNumber;
            currentTableSortBy.current = sortedBy;
            currentTableSortDir.current = sortedDir;
            currentSearchTerm.current = debouncedSearchTerm;
            currentAppliedFilters.current = appliedFilters;
            currentAccounts.current = reduxAccounts?.sort()?.join(',');
            currentStatus.current = selectedDeadlineStatus;

            setIsLoading(true);

            const mappedFilters = mapFilters();

            const pageSize = 50;

            const searchRequest = {
                requestCount: pageSize,
                pageNumber: pageNumber,
                sortField: sortedBy.sortName,
                sortDirection: sortedDir,
                filters: mappedFilters,
                searchTerm: debouncedSearchTerm,
            };

            let data = [];

            const formatDateColour = function (date) {
                if (pcnStateToShow === PcnListingType.CLOSED) {
                    return {};
                }

                let checkDate = moment(date);
                let now = moment();
                const daysTilDeadline = 3;
                let approachingDeadline = moment().add(daysTilDeadline, 'days')

                if (checkDate.isBefore(now)) {
                    return { color: '#F0578A', fontWeight: 'bold' }
                }

                if (checkDate.isBefore(approachingDeadline)) {
                    return { color: '#FF9076' };
                }

                return {};
            }

            PcnService.searchPcns(searchRequest)
                .then((response) => {
                    if (response.data != null) {
                        data = response.data.map((d, i) => {
                            const url = isOnHold ? `/pcn-admin/on-hold/${d.id}` : `${window.env.PCN_URL}/pcndetail?pcnId=${d.id}`;

                            return [
                                isOnHold ? <Link key={`link-${d.id}`} to={url}>{d.pcnNumber}</Link> : <a key={`link-${d.id}`} href={url}>{d.pcnNumber}</a>,
                                <span key={`account-${d.id}`}>{d.memberNumber} - {d.account}</span>,
                                ...(isOnHold ? [] : [<span key={`review-date-${d.id}`} style={formatDateColour(d.reviewDate)}><DateToLocal format="DD MMM YYYY">{d.reviewDate ?? "-"}</DateToLocal></span>]),
                                <span key={`payment-date-${d.id}`} style={formatDateColour(d.paymentDueDate)}><DateToLocal format="DD MMM YYYY">{d.paymentDueDate ?? "-"}</DateToLocal></span>,
                                <DateToLocal key={`issue-date-${d.id}`} format="DD MMM YYYY">{d.issueDate ?? "-"}</DateToLocal>,
                                d.contraventionCode,
                                issueTypes?.find(x => x.key === d.issueType)?.name ?? "-",
                                authorities?.find(x => x.key === d.issueAuthority)?.name ?? "-",
                                d.registration,
                                d.assetLocation,
                                ...(isOnHold ? [] : [
                                    statuses?.find(x => x.key === d.status)?.name ?? "-",
                                    statusComments?.find(x => x.key === d.statusComment)?.name ?? "-"]),
                                <DateToLocal key={`received-date-${d.id}`} format="DD MMM YYYY">{d.receivedDate ?? "-"}</DateToLocal>,
                            ];
                        });
                    }

                    setDeadlineStatuses((prev) => {
                        return prev.map((ds) => {
                            return {
                                ...ds,
                                count: response.deadlineStatusCounts.find(dsc => dsc.id === ds.id)?.count ?? 0
                            }
                        });
                    })

                    setTotalRecords(response.totalCount);
                    setTableRows(data);
                }, (error) => {
                    if (error.response.status === badRequestCode) {
                        NotificationToaster.show(Intent.WARNING, error.response.data);
                    } else {
                        NotificationToaster.show(Intent.DANGER, generalErrorMessage);
                    }
                })
                .catch(() => {
                    NotificationToaster.show(Intent.DANGER, generalErrorMessage);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }

    function mapFilters() {
        let mappedFilters = (currentAppliedFilters.current ?? []).map(function (f) {
            return {
                key: f.name,
                ...f,
            };
        });

        if (reduxAccounts != null && !isOnHold) {
            const mappedAccounts = reduxAccounts.map(function (l) {
                return { key: "ACCOUNT", value: l };
            });

            mappedFilters = [...mappedFilters, ...mappedAccounts];
        }

        mappedFilters.push({ key: "STATE", value: pcnStateToShow })

        if (currentStatus.current != null) {
            mappedFilters = [
                ...mappedFilters,
                {
                    key: "DEADLINESTATUS",
                    value: currentStatus.current,
                },
            ];
        }

        return mappedFilters;
    }

    useEffect(() => {
        fetchTableData(false);
    }, [
        pageNumber,
        sortedBy,
        sortedDir,
        debouncedSearchTerm,
        appliedFilters,
        initalSetupComplete,
        selectedDeadlineStatus,
    ]);

    function onAccountsListClose() {
        fetchTableData(false);
    }

    return (
        <>
            <div className="filter-controls">
                <div className="inline-items spacer-bottom-small ">
                    <div className="inline-item fill">
                        <div className="form-field-full">
                            <FormTextInput
                                inputRef={inputSearchRef}
                                placeholder="Search PCN Number, Location or Registration"
                                onChange={onSearchChange}
                                value={searchTerm}
                                large
                                disabled={isLoading && inputSearchRef.current !== document?.activeElement}
                                icon="search"
                                id="pcn-search-field"
                            />
                        </div>
                    </div>
                    <ShowHide
                        evaluator={isOnHold}
                        hide={
                            <div className="inline-item">
                                <FormListSelector
                                    itemsList={accounts}
                                    selectedItems={selectedAccounts}
                                    setSelectedItems={onAccountsSelected}
                                    searchPlaceholder="Account Name or Member Number"
                                    itemsName="Accounts"
                                    itemsIcon='inbox'
                                    loading={isLoading}
                                    onListClose={onAccountsListClose}
                                />
                            </div>
                        }
                    />
                    {filterName && <div className="inline-item">
                        <FilterButton onToggle={onToggleFilter} filters={appliedFilters} />                                    
                    </div>}
                </div>
                {filterName && <ShowHide
                    evaluator={!isOnHold}
                    show={<Filter
                        filterName={filterName}
                        visible={showFilters}
                        filters={pageFilters}
                        onUpdate={onFilterChange}
                        onClickOutside={onToggleFilter}
                    />}
                />}
            </div>
            <ShowHide
                evaluator={pcnStateToShow === PcnListingType.OPEN}
                show={
                    <div className="spacer-bottom">
                        <FormToggleList
                            disabled={isLoading}
                            items={deadlineStatuses}
                            value={selectedDeadlineStatus}
                            onChange={onDeadlineStatusChange}
                        />
                    </div>
                }
            />
            <ListingTable
                showPerPage={false}
                pageSize={50}
                headers={tableHeaders}
                data={tableRows}
                totalRecordCount={totalRecords}
                loadingData={isLoading}
                onPagingChange={onPagingChange}
                sortable
                sortableHeaders={sortableHeaders}
                sortedBy={sortedBy}
                sortedDir={sortedDir}
                onSort={onTableSort}
                noDataMessage={pcnStateToShow === PcnListingType.ONHOLD ? "There are currently no PCNs on hold" : "Please check your filters and selected accounts"}
            />
        </>
    );

}


PcnListingResults.propTypes = {
    pcnStateToShow: PropTypes.string
};